import AccessibilityUtilities from "../utilities/accessibility.ts";
// @ts-ignore
import Headroom from "headroom.js";
const mobileMQ = window.matchMedia("(max-width: 1199px)");

export class SiteHeader {
  private element: HTMLElement;
  private headroom: Headroom;
  private openButton: HTMLButtonElement;
  private parentNavItems: NodeListOf<HTMLElement>;
  private subNavs: NodeListOf<HTMLElement>;
  private subnavToggleLinks: NodeListOf<HTMLElement>;
  private searchTrigger: HTMLElement;
  private searchForm: HTMLFormElement | null;
  private formInput: HTMLFormElement | null;
  public visible: boolean = false;
  private searchVisible: boolean = false;
  private selectedMainNavSectionIndex: number | null = null;
  private mobileSubnavBackButtons: NodeListOf<HTMLButtonElement>;
  private secondaryMenu: HTMLElement | null;
  private audienceMenuTriggerDesktop: HTMLButtonElement;
  private audienceMenu: HTMLElement | null;
  private audienceMenuVisible: boolean = false;
  private skipLink: HTMLAnchorElement | null;
  private mainPageContent: HTMLElement | null;
  private isHybridVariant: boolean;

  // MARK: Constructor
  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      const openButtonAnchorElement = document.querySelector(
        `[aria-controls="${this.element.id}"]`
      ) as HTMLAnchorElement;
      this.openButton = AccessibilityUtilities.convertAnchorToButton(
        openButtonAnchorElement
      );
      this.parentNavItems = this.element.querySelectorAll(
        ".site-header__mega-menu-main-nav > ul > li"
      );
      this.subNavs = this.element.querySelectorAll(
        ".site-header__mega-menu-main-nav-subnav"
      );
      this.subnavToggleLinks = this.element.querySelectorAll(
        ".site-header__mega-menu-main-nav > ul > li > a"
      );
      const searchTriggerAnchorElement = document.querySelector(
        ".site-header__search-toggle"
      ) as HTMLAnchorElement;
      if (searchTriggerAnchorElement) {
        this.searchTrigger = AccessibilityUtilities.convertAnchorToButton(
          searchTriggerAnchorElement
        );
      }
      this.searchForm = document.querySelector(".site-header__search-form");
      this.formInput = document.querySelector(
        ".site-header__search-form #search"
      );
      this.mobileSubnavBackButtons = this.element.querySelectorAll(
        ".site-header__mega-menu-main-nav-subnav-back"
      );
      this.secondaryMenu = this.element.querySelector(
        ".site-header__mega-menu-secondary"
      );
      this.skipLink = document.querySelector(".skip-link");
      this.mainPageContent = document.querySelector("main");
      const audienceTrigger = document.querySelector(
        ".site-header__audience-trigger"
      ) as HTMLAnchorElement;
      if (audienceTrigger) {
        this.audienceMenuTriggerDesktop =
          AccessibilityUtilities.convertAnchorToButton(audienceTrigger);
      }
      this.audienceMenu = document.querySelector(
        ".site-header__top .site-header__audience-inner"
      );
      this.isHybridVariant =
        document
          .querySelector(".site-header")
          ?.classList.contains("site-header--hybrid") || false;
      this.init();
    }
  }

  // MARK: Init
  private init() {
    this.initialState();
    this.handleResize();
    this.handleEsc();
    this.handleOpenButtonClick();
    this.handleTransitionEnd();
    this.handleSubnavToggleClicks();
    this.handleMobileSubnavBackButtons();
    this.handleOutsideClick();
    this.handleSkipLinkClick();
    this.handleSearch();
    this.closeSearchEscape();
    this.closeSearchOutsideClick();
    this.handleAudienceMenu();
    if (this.searchTrigger) {
      this.searchTabPast();
    }
  }

  private initStickyNav() {
    const siteHeaderRoot = document.querySelector(
      "body:has(.home-hero) .site-header"
    ) as HTMLElement;
    this.headroom = new Headroom(siteHeaderRoot, {
      tolerance: 5,
    });
    if (mobileMQ.matches && siteHeaderRoot) {
      this.headroom.init();
    }
  }

  // MARK: Resize
  private handleResize() {
    const resize = () => {
      this.handleStates();
      this.initStickyNav();
      if (this.searchTrigger) {
        this.moveSearchTrigger();
      }
    };
    mobileMQ.addEventListener("change", resize);
    resize();
  }

  // MARK: Initial State
  private initialState() {
    this.openButton.setAttribute("aria-expanded", "false");
    this.subNavs.forEach((subNav) => {
      subNav.setAttribute("aria-hidden", "true");
    });
  }

  // MARK: Handle States
  private handleStates() {
    // if desktop, hidden, not hybrid variant
    if (!mobileMQ.matches && !this.visible && !this.isHybridVariant) {
      this.element.setAttribute("aria-hidden", "false");
    } else if (mobileMQ.matches && !this.visible && !this.isHybridVariant) {
      this.element.setAttribute("aria-hidden", "true");
    }

    // if hamburger is opened, it's desktop, not hybrid variant
    if (!mobileMQ.matches && this.visible && !this.isHybridVariant) {
      this.toggleHamburgerVisibility();
      this.element.setAttribute("aria-hidden", "false");
    }

    // if mobile and subnav is open - set to close
    this.subNavs.forEach((subNav) => {
      if (
        mobileMQ.matches &&
        !this.visible &&
        subNav.getAttribute("aria-hidden") === "false"
      ) {
        this.toggleSubnavSections(this.selectedMainNavSectionIndex, true, null);
        this.selectedMainNavSectionIndex = null;
      }
    });

    // if desktop, visible, set secondary menu to aria hidden false
    if (!mobileMQ.matches && this.visible) {
      this.secondaryMenu?.setAttribute("aria-hidden", "false");
    }
  }

  // MARK: Toggle Hamburger Visibility
  private toggleHamburgerVisibility() {
    // if search hamburger is closed and search is clicked
    if (!this.visible && this.searchVisible) {
      this.toggleSearch();
    }
    this.visible = !this.visible;
    this.openButton.setAttribute("aria-expanded", `${this.visible}`);
    this.element.setAttribute("aria-hidden", `${!this.visible}`);
    this.secondaryMenu?.setAttribute("aria-hidden", `${!this.visible}`);
    this.element.classList.add("transitioning");

    // update body classes (desktop only)
    if (!mobileMQ.matches) {
      this.visible
        ? document.body.classList.add("body--scroll-lock")
        : document.body.classList.remove("body--scroll-lock");
    }

    if (mobileMQ.matches) {
      this.bodyScrollLock(this.visible);
    }

    // Scroll to top if hybrid
    if (this.isHybridVariant) {
      if (this.visible) {
        if (!mobileMQ.matches) {
          document.documentElement.scrollTop = 0;
        }
      }
    }

    // toggle <main> element to be inert depending on overlay visibility
    this.visible
      ? this.mainPageContent?.setAttribute("inert", "")
      : this.mainPageContent?.removeAttribute("inert");

    // on mobile if subnav is opened and menu closed, reset the menu
    if (!this.visible) {
      this.subNavs.forEach((subnav) => {
        if (subnav.getAttribute("aria-hidden") === "false") {
          subnav.setAttribute("aria-hidden", "true");
          const topLink = subnav.parentElement?.querySelector(
            ".site-header__mega-menu-main-top-link"
          );
          if (topLink) {
            topLink.setAttribute("aria-expanded", "false");
          }
        }
      });
    }
  }

  // MARK: Hamburger Event
  private handleOpenButtonClick() {
    this.openButton.addEventListener("click", () => {
      this.toggleHamburgerVisibility();
    });
  }

  // MARK: Escape Key Press
  private handleEsc() {
    document.addEventListener("keydown", (event) => {
      const key = event.key || event.keyCode;
      // Close the nav when the esc key is pressed while it's open
      if (key === "Escape" || key === "Esc" || key === 27) {
        if (this.visible) {
          this.toggleHamburgerVisibility();
        }
      }
    });
  }

  // MARK: Transition End
  private handleTransitionEnd() {
    this.element.addEventListener("transitionend", (event) => {
      if (event.target === event.currentTarget) {
        if (this.element.classList.contains("transitioning")) {
          this.element.classList.remove("transitioning");
        }
      }
    });
  }

  // MARK: Body Scroll lock
  private bodyScrollLock(visibility: boolean) {
    const htmlElement = document.documentElement;
    if (visibility) {
      const offsetY = window.scrollY;
      document.body.style.top = `${-offsetY}px`;
      document.body.classList.add("body--scroll-lock");
      htmlElement.style.scrollBehavior = "initial";
    } else {
      const offsetY = Math.abs(parseInt(document.body.style.top || "0", 10));
      document.body.classList.remove("body--scroll-lock");
      document.body.style.removeProperty("top");
      window.scrollTo(0, offsetY || 0);
      htmlElement.style.removeProperty("scroll-behavior");
    }
  }

  // MARK: Subnav Toggle Clicks
  private handleSubnavToggleClicks() {
    const subnavToggleLinks = Array.from(
      this.subnavToggleLinks
    ) as HTMLAnchorElement[];
    subnavToggleLinks.forEach(
      (parentNavItemLink: HTMLAnchorElement, parentIndex) => {
        const correspondingSubnav =
          parentNavItemLink.nextElementSibling as HTMLElement;
        if (!correspondingSubnav) return;

        const parentNavItemLinkButton =
          AccessibilityUtilities.convertAnchorToButton(parentNavItemLink);
        parentNavItemLinkButton.setAttribute("aria-expanded", "false");

        parentNavItemLinkButton.addEventListener("click", (e: Event) => {
          const target = e.currentTarget as HTMLElement;

          // Close search if open
          if (this.searchVisible) {
            this.toggleSearch();
          }

          const isCurrentlyExpanded =
            parentNavItemLinkButton.getAttribute("aria-expanded") === "true";

          // Toggle subnav sections
          this.toggleSubnavSections(
            this.selectedMainNavSectionIndex,
            isCurrentlyExpanded,
            target
          );

          // Handle desktop - close menu on tabbing past last item
          if (!isCurrentlyExpanded && !mobileMQ.matches) {
            this.handleDesktopLastTabClose();
          }

          // Update selected index
          this.selectedMainNavSectionIndex = isCurrentlyExpanded
            ? null
            : parentIndex;
        });
      }
    );
  }

  // MARK: Mobile Subnav Back Buttons
  private handleMobileSubnavBackButtons() {
    this.mobileSubnavBackButtons.forEach((mobileSubnavBackButton) => {
      mobileSubnavBackButton.addEventListener("click", () => {
        const parentSubNav = mobileSubnavBackButton.closest(
          ".site-header__mega-menu-main-nav-subnav"
        ) as HTMLElement;
        const parentNavItemLinkButton =
          parentSubNav.previousElementSibling as HTMLButtonElement;
        parentSubNav.setAttribute("aria-hidden", "true");
        parentNavItemLinkButton.setAttribute("aria-expanded", "false");
        this.selectedMainNavSectionIndex = null;
        this.openButton.focus();
        this.secondaryMenu?.setAttribute("aria-hidden", "false");
      });
    });
  }

  // MARK: Toggle Subnav Sections
  private toggleSubnavSections(
    index: number | null,
    isCurrentlyVisible: boolean,
    target: EventTarget | null
  ) {
    if (!target || !(target instanceof Element)) return;

    const navItems = Array.from(
      this.element.querySelectorAll(".site-header__mega-menu-main-top-link")
    );

    // Toggle subnav state
    const toggleSubnavState = (
      linkElement: Element,
      subnavElement: HTMLElement,
      shouldExpand: boolean
    ) => {
      linkElement.setAttribute("aria-expanded", `${shouldExpand}`);
      subnavElement.setAttribute("aria-hidden", `${!shouldExpand}`);
    };

    // When a subnav is currently open
    if (index !== null) {
      const currentParentItem = this.parentNavItems[index];
      const currentParentLink = currentParentItem.querySelector("button, a");
      const currentSubnav = currentParentItem.querySelector(
        ".site-header__mega-menu-main-nav-subnav"
      ) as HTMLElement;

      if (!currentParentLink || !currentSubnav) return;

      // Toggle current subnav
      toggleSubnavState(currentParentLink, currentSubnav, !isCurrentlyVisible);

      // If a subnav was already open and another clicked
      if (
        target !== currentParentLink &&
        target.classList.contains("site-header__mega-menu-main-top-link")
      ) {
        const targetSubnav = (target as HTMLElement)
          .nextElementSibling as HTMLElement;

        // Reset current subnav
        toggleSubnavState(currentParentLink, currentSubnav, false);

        // Toggle new subnav
        toggleSubnavState(target, targetSubnav, true);

        // Update selected index
        this.selectedMainNavSectionIndex = navItems.indexOf(target);
      }
    }
    // When no subnav is currently open
    else {
      // Ensure the target is a nav item
      if (target.classList.contains("site-header__mega-menu-main-top-link")) {
        const targetSubnav = (target as HTMLElement)
          .nextElementSibling as HTMLElement;

        // Toggle target subnav
        toggleSubnavState(target, targetSubnav, true);

        // Update selected index
        this.selectedMainNavSectionIndex = navItems.indexOf(target);

        // Handle mobile secondary menu
        if (mobileMQ.matches) {
          this.secondaryMenu?.setAttribute(
            "aria-hidden",
            `${!isCurrentlyVisible}`
          );
        }
      }
    }
  }

  // MARK: Dropdown Outside Click
  private handleOutsideClick() {
    document.body.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      const isClickInsideDropdown = Array.from(this.subNavs).some((container) =>
        container.contains(target)
      );

      if (
        !isClickInsideDropdown &&
        !target.closest(".site-header__mega-menu-main-top-link") &&
        !mobileMQ.matches
      ) {
        this.toggleSubnavSections(
          this.selectedMainNavSectionIndex,
          true,
          target
        );
        this.selectedMainNavSectionIndex = null;
      }
    });
  }

  // MARK: Desktop Tab Close
  private handleDesktopLastTabClose() {
    // close the opened dropdown when the last item is selected and the tab key is pressed
    this.subNavs.forEach((subNav) => {
      if (!mobileMQ.matches && subNav.getAttribute("aria-hidden") === "false") {
        const lastDropdownLI = subNav.querySelector(
          "li:last-child"
        ) as HTMLElement;
        const lastDropdownLink = lastDropdownLI.querySelector(
          ".site-header__mega-menu-main-sub-link"
        ) as HTMLAnchorElement;
        lastDropdownLink.addEventListener("keydown", (event) => {
          if (event.key === "Tab" && !event.shiftKey) {
            // close the currently open dropdown
            this.toggleSubnavSections(
              this.selectedMainNavSectionIndex,
              true,
              event.target
            );
            this.selectedMainNavSectionIndex = null;
          }
        });
      }
    });
  }

  // MARK: Search Toggle
  private toggleSearch() {
    if (this.visible && !this.searchVisible) {
      this.toggleHamburgerVisibility();
    }

    this.searchVisible = !this.searchVisible;
    this.searchVisible
      ? document.body.classList.add("body--scroll-lock-search")
      : document.body.classList.remove("body--scroll-lock-search");
    this.searchVisible
      ? this.searchTrigger.setAttribute("aria-expanded", "true")
      : this.searchTrigger.setAttribute("aria-expanded", "false");
    this.searchVisible
      ? this.searchForm?.setAttribute("aria-hidden", "false")
      : this.searchForm?.setAttribute("aria-hidden", "true");
    if (this.searchVisible) {
      setTimeout(() => {
        this.formInput?.focus();
      }, 300);
    }

    this.bodyScrollLock(this.searchVisible);
  }
  // MARK: Search Handler
  private handleSearch() {
    if (this.searchTrigger) {
      this.searchForm?.setAttribute("aria-hidden", "true");
      this.searchTrigger.setAttribute("aria-expanded", "false");
      this.searchTrigger.addEventListener("click", () => {
        this.toggleSearch();
      });
    }
  }

  // MARK: Search Escape Key
  private closeSearchEscape() {
    document.addEventListener("keydown", (event) => {
      const key = event.key || event.keyCode;
      if (key === "Escape" || key === "Esc" || key === 27) {
        if (this.searchVisible) {
          this.toggleSearch();
        }
      }
    });
  }

  // MARK: Search Tab Past
  private searchTabPast() {
    const lastTabbableSearchItem = this.searchForm?.querySelector(
      ".site-header__search-form-field-wrap button[type='submit']"
    ) as HTMLAnchorElement;
    if (lastTabbableSearchItem) {
      lastTabbableSearchItem.addEventListener("keydown", (event) => {
        if (event.key === "Tab" && !event.shiftKey) {
          if (this.searchVisible) {
            this.toggleSearch();
          }
        }
      });
    }
  }

  // MARK: Search Outside Click
  private closeSearchOutsideClick() {
    document.body.addEventListener("click", (event) => {
      const target = event.target as HTMLElement;
      if (this.searchVisible && !target.closest(".site-header")) {
        this.toggleSearch();
      }
    });
  }

  // MARK: Skip Link
  private handleSkipLinkClick() {
    this.skipLink?.addEventListener("click", () => {
      if (this.visible) {
        this.toggleHamburgerVisibility();
      }
      if (this.searchVisible) {
        this.toggleSearch();
      }
    });
  }
  // MARK: Audience Menu
  private handleAudienceMenu() {
    if (this.audienceMenu) {
      this.audienceMenu.setAttribute("aria-hidden", "true");
      this.audienceMenuTriggerDesktop.setAttribute("aria-expanded", "false");
      const toggleAudienceMenu = () => {
        this.audienceMenuVisible = !this.audienceMenuVisible;
        // close if search clicked
        if (this.searchVisible && this.audienceMenuVisible) {
          this.toggleSearch();
        }
        this.audienceMenuVisible
          ? this.audienceMenu?.setAttribute("aria-hidden", "false")
          : this.audienceMenu?.setAttribute("aria-hidden", "true");
        this.audienceMenuVisible
          ? this.audienceMenuTriggerDesktop.setAttribute(
              "aria-expanded",
              "true"
            )
          : this.audienceMenuTriggerDesktop.setAttribute(
              "aria-expanded",
              "false"
            );
      };
      // add event listener to trigger
      this.audienceMenuTriggerDesktop.addEventListener("click", () => {
        toggleAudienceMenu();
      });
      // add event listener to escape key press
      document.addEventListener("keydown", (event) => {
        const key = event.key || event.keyCode;
        if (key === "Escape" || key === "Esc" || key === 27) {
          if (this.audienceMenuVisible) {
            toggleAudienceMenu();
          }
        }
      });
      // close after tabbing past last link
      const lastAudienceInnerLink = this.audienceMenu.querySelector(
        ".site-header__audience-inner ul li:last-child a"
      ) as HTMLAnchorElement;
      if (lastAudienceInnerLink) {
        lastAudienceInnerLink.addEventListener("keydown", (event) => {
          if (event.key === "Tab" && !event.shiftKey) {
            if (this.audienceMenuVisible) {
              toggleAudienceMenu();
            }
          }
        });
      }
      // close on outside click
      document.body.addEventListener("click", (event) => {
        const target = event.target as HTMLElement;
        if (this.audienceMenuVisible) {
          if (
            !target.closest(".site-header__audience-inner") &&
            !target.closest(".site-header__audience-trigger")
          ) {
            toggleAudienceMenu();
          }
        }
      });
    }
  }

  // MARK: Move Search Trigger
  private moveSearchTrigger() {
    if (
      this.searchTrigger.classList.contains("site-header__search-toggle--main")
    ) {
      const parentElement = this.parentNavItems[0].parentNode as HTMLElement;
      const siteHeaderTop = document.querySelector(
        ".site-header__top"
      ) as HTMLElement;

      if (this.isHybridVariant) {
        const hybridMenu = document.querySelector(
          ".site-header__hyrbrid-menu"
        ) as HTMLElement;
        if (!mobileMQ.matches) {
          hybridMenu.appendChild(this.searchTrigger);
        } else {
          if (siteHeaderTop) {
            siteHeaderTop.appendChild(this.searchTrigger);
          }
        }
      } else {
        if (!mobileMQ.matches) {
          const li = document.createElement("li");
          li.appendChild(this.searchTrigger);
          if (parentElement) {
            parentElement.appendChild(li);
          }
        } else {
          const parentLi = this.searchTrigger.closest("li");
          if (parentLi) {
            parentLi.remove();
          }
          if (siteHeaderTop) {
            siteHeaderTop.appendChild(this.searchTrigger);
          }
        }
      }
    }
  }
}

// MARK: Hybrid Variation
export class SiteHeaderHybrid {
  private element: HTMLElement;
  private priorityLinkNav: HTMLElement | null;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.priorityLinkNav = document.querySelector(
        ".site-header__priority-links"
      );
      this.init();
    }
  }

  private init() {
    this.detectOverlayOpen();
  }

  private detectOverlayOpen() {
    var elemToObserve = this.element;
    var prevAriaHiddenState = elemToObserve.getAttribute("aria-hidden");
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === "aria-hidden") {
          const targetElement = mutation.target as Element;
          var currentAriaHiddenState =
            targetElement.getAttribute("aria-hidden");
          if (prevAriaHiddenState !== currentAriaHiddenState) {
            prevAriaHiddenState = currentAriaHiddenState;
            this.handleOverlayToggle(currentAriaHiddenState);
          }
        }
      });
    });

    observer.observe(elemToObserve, {
      attributes: true,
      attributeFilter: ["aria-hidden"],
    });
  }

  private handleOverlayToggle(currentAriaHiddenState: any) {
    if (currentAriaHiddenState === "true") {
      this.priorityLinkNav?.setAttribute("aria-hidden", "false");
    } else {
      this.priorityLinkNav?.setAttribute("aria-hidden", "true");
    }
  }
}

// MARK: Export
export default function siteHeaderInit() {
  // Main Site Header
  const header = document.querySelector(
    ".site-header__mega-menu"
  ) as HTMLElement;
  new SiteHeader(header);

  // Hybrid Variant
  const headerHybrid = document.querySelectorAll(
    ".site-header--hybrid .site-header__mega-menu"
  ) as NodeListOf<HTMLElement>;

  for (let i = 0; i < headerHybrid.length; i++) {
    new SiteHeaderHybrid(headerHybrid[i]);
  }
}
